// Aceite pela própria pessoa (Parte 5.7 `POST /me/delegations/{id}/accept`; REQ-AUTHZ-008; step-up): o delegado vê o que o PI assinou, o significado "Aceito as tarefas
// delegadas" e o hash; ao concluir a delegação fica `Active`, o papel de estudo é materializado e o `/me` é recarregado (o estudo passa a aparecer nos contextos).
// Quem assinou como PI nunca aceita a mesma delegação (SoD, 422); as travas são reavaliadas (423 com os motivos).
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../../lib/auth/authContext';
import { type Delegation, useAcceptDelegation } from '../../api/delegations';
import { SignatureConfirmDialog } from './SignatureConfirmDialog';

export interface AcceptDelegationDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly delegation: Delegation;
  readonly onDone?: ((message: string) => void) | undefined;
}

export function AcceptDelegationDialog({
  open,
  onOpenChange,
  delegation,
  onDone,
}: AcceptDelegationDialogProps) {
  const { t } = useTranslation('authz');
  const { refreshMe } = useAuth();
  const accept = useAcceptDelegation();
  // O delegado não lê o catálogo (`delegation.read`): os nomes das tarefas vêm da própria delegação que ele aceita.
  const taskNames = useMemo(
    () =>
      new Map(
        delegation.tasks.flatMap((task): [string, string][] =>
          task.name === null ? [] : [[task.taskCode, task.name]],
        ),
      ),
    [delegation.tasks],
  );
  return (
    <SignatureConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          accept.reset();
        }
        onOpenChange(next);
      }}
      delegation={delegation}
      taskNames={taskNames}
      mode="accept"
      pending={accept.isPending}
      error={accept.error}
      onConfirm={async (input) => {
        await accept.mutateAsync({ delegationId: delegation.id, input });
        onOpenChange(false);
        // O aceite materializa o papel de estudo: `/me` passa a entregar o contexto/estudo da delegação.
        await refreshMe().catch(() => undefined);
        onDone?.(t('delegations.accept.accepted'));
      }}
    />
  );
}
